/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2023-02-03 12:00:00
 *
 *******************************************************/

import _ from 'lodash';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Page, Button, Icon, Stack, Layout, Link, TextStyle, Banner } from '@shopify/polaris';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import ThemeSetupForm from './theme-setup-form';
import { ExternalMinor } from '@shopify/polaris-icons';
import * as recommendationAction from 'src/stores/actions/recommendation-actions';
import WarningLiveThemeModal from 'src/components/views/theme-setup-v3/theme-setup-form/warning-live-theme-modal';
import WarningOS10Modal from 'src/components/views/theme-setup-v3/theme-setup-form/warning-os10-modal';
import ContactUsModal from './contact-us-modal';
import { confirmLeavePage } from 'src/helpers';
import ConfirmLeaveModal from 'src/components/views/common/handle-leave/confirm-leave-modal';
import RouteLeavingGuard from 'src/components/views/common/handle-leave/route-leaving-guard';
import { isDirty } from 'redux-form';
import * as themeSetupV3Action from 'actions/theme-setup-v3-actions';
import { URL } from 'src/constants/links';
import { TypeScreen } from './index';
import { routes } from 'src/routes';
import { getThemeReviewUrl, getFullAppUrlThemeEditor, getFullAppUrlThemes } from 'src/helpers';
import * as checklistActions from 'actions/dashboard/checklist-actions';
import { trackFSEvent } from 'src/integrations/fullStory';
import { enableThemeSetupV3OnLiveTheme } from 'src/helpers/theme-setup-helper';

type Props = {
  installStatus: string,
  installAppStatus: string,
  themeSetupV2: boolean
};

type ComposedProps = Props & WithI18nProps;

type State = {
  toggleThemes: boolean,
  youtubeModal: boolean,
  step: number,
  isNextableStep: boolean,
  isSetuppable: boolean,
  allRecommendationWidgets: Array<Record<String, any>>,
  dataForm: any,
  activeModalWarningLiveTheme: boolean,
  activeModalWarningOS10: boolean,
  activeModalConfirmLeavePage: boolean,
  activeModalContactUs: boolean,
  isSettingUp: boolean,
  page: string,
  // This value to restore data to form after submit and back again
  formData: any,
  isBackAfterSubmit: boolean,
  isDismissBannerComplete: boolean
};

const PageType = {
  SetupForm: 'SetupForm',
  Success: 'Success',
  Unsuccess: 'Unsuccess'
};

class ThemeSetupScreenSetup extends Component<ComposedProps, State> {
  state = {
    toggleThemes: false,
    youtubeModal: false,
    step: 1,
    isNextableStep: false,
    isSetuppable: false,
    allRecommendationWidgets: this.props.allRecommendationWidgets,
    dataForm: {},
    activeModalWarningLiveTheme: false,
    activeModalWarningOS10: false,
    activeModalConfirmLeavePage: false,
    activeModalContactUs: false,
    isSettingUp: false,
    page: PageType.SetupForm,
    formData: {},
    isBackAfterSubmit: false,
    isDismissBannerComplete: false,
    themeSelected: null
  };

  // Toggle change state
  toggleChangeState = (id: string) => {
    const value = this.state[id];
    this.setState({ [id]: !value });
  };

  onCheckNextableStep = (isNextableStep, isSetuppable, dataForm, formData, themeCheck) => {
    this.setState({
      isNextableStep,
      isSetuppable,
      dataForm,
      formData,
      themeSelected: themeCheck
    });
  };

  onNext = () => {
    const { step, dataForm = {} } = this.state;
    const { currentThemeSelected } = dataForm;

    if (currentThemeSelected && currentThemeSelected.role === 'main') {
      this.toggleChangeState('activeModalWarningLiveTheme');
    } else {
      this.setState({
        step: step + 1
      });
    }
  };

  handleSetupOnLiveTheme = () => {
    const { step } = this.state;
    this.setState({
      step: step + 1,
      activeModalWarningLiveTheme: false
    });
  };

  handleClickCancel = () => {
    if (this.state.step === 2) {
      this.setState({
        step: 1,
        isNextableStep: true
      });
    } else {
      this.confirmLeave(TypeScreen.List);
    }
  };

  componentDidMount() {
    const { getRecommendationWidgets, getAllThemes } = this.props;
    getAllThemes();
    getRecommendationWidgets();

    // Handle when reload or leave page
    window.addEventListener('beforeunload', this.confirmLeavePage);
    window.addEventListener('keydown', this.keyDownHandler);
  }

  keyDownHandler = event => {
    if (event.key === 'Enter') {
      event.preventDefault();
    }
  };

  // Handle confirm leave page
  confirmLeavePage = event => {
    const { generalFormDirty, i18n } = this.props;
    return confirmLeavePage(event, generalFormDirty, i18n);
  };

  // Close modal
  closeModal = () => {
    this.setState({ activeModalConfirmLeavePage: false, actionToGo: () => {}, params: {} });
  };

  // Handle confirm leave modal
  // Discard change, continue action
  handleDiscard = () => {
    const { actionToGo } = this.state;
    actionToGo();

    // Close modal
    this.closeModal();
  };

  // Handle confirm leave
  confirmLeave = (action, params = {}) => {
    const { generalFormDirty, handleThemeSetupChangeScreen } = this.props;
    let callBack = (params = {}) => {};
    switch (action) {
      case TypeScreen.List: {
        callBack = () => handleThemeSetupChangeScreen(TypeScreen.List);
        break;
      }
      default:
    }
    if (generalFormDirty) {
      this.setState({ activeModalConfirmLeavePage: true, actionToGo: callBack, params: params });
    } else {
      // $FlowFixMe
      callBack(params);
    }
  };

  componentWillReceiveProps(nextProps) {
    if (this.props.themeSelected !== nextProps.themeSelected) {
      this.setState({
        themeSelected: nextProps.themeSelected
      });
    }

    this.setState({
      isDismissBannerComplete: false
    });

    if (nextProps.allRecommendationWidgets !== this.props.allRecommendationWidgets) {
      this.setState({
        allRecommendationWidgets: nextProps.allRecommendationWidgets
      });
    }

    if (
      nextProps.setupThemeSuccess &&
      nextProps.setupThemeSuccess !== this.props.setupThemeSuccess
    ) {
      this.setState({
        page: PageType.Success,
        isSettingUp: false
      });
    } else if (
      nextProps.setupThemeSuccess === false &&
      nextProps.setupThemeSuccess !== this.props.setupThemeSuccess
    ) {
      this.setState({
        page: PageType.Unsuccess,
        isSettingUp: false
      });
    }
  }

  onSetup = () => {
    const { dataForm, themeSelected } = this.state;
    const { setupThemeV3, trackEvent, i18n, themeCheckCompatible, themeCheckCompatibleId } =
      this.props;

    const position = _.get(dataForm, 'currentPositionSelect', null);
    const features = _.get(dataForm, 'currentFeatureSelected', {}) || {};
    const widgets = _.get(dataForm, 'currentWidgetSelect', {}) || {};
    const recommendationWidgets = _.reduce(
      _.filter(_.keys(widgets), widget => widgets[widget]),
      (result, widgetId) => {
        if (widgetId.includes('collectionpage')) {
          if (!result['collection-page']) {
            result['collection-page'] = [];
          }
          result['collection-page'].push(widgetId);
        }
        if (widgetId.includes('cartpage')) {
          if (!result['cart-page']) {
            result['cart-page'] = [];
          }
          result['cart-page'].push(widgetId);
        }
        if (widgetId.includes('productpage')) {
          if (!result['product-page']) {
            result['product-page'] = [];
          }
          result['product-page'].push(widgetId);
        }
        if (widgetId.includes('homepage')) {
          if (!result['home-page']) {
            result['home-page'] = [];
          }
          result['home-page'].push(widgetId);
        }
        return result;
      },
      {}
    );

    let layout = '';
    if (position && position[0]) {
      layout = position[0] === 'v' ? 'vertical' : 'horizontal';
    } else {
      if (_.get(themeSelected, 'taeFeatures.filterCollection', '') === 'installed') {
        layout = _.get(themeSelected, 'taeFeatures.filterLayout.filterSearch', '');
      }
      if (_.get(themeSelected, 'taeFeatures.filterSearch', '') === 'installed') {
        layout = _.get(themeSelected, 'taeFeatures.filterLayout.filterCollection', '');
      }
    }

    const filterLayout = {
      collection: features?.filterCollection ? layout : '',
      search: features?.filterSearch ? layout : ''
    };

    const taeFeatures = {
      filterLayout,
      recommendationWidgets
    };

    if (features?.recommendation) {
      taeFeatures.recommendation = 'checked';
    }
    if (features?.instantSearch) {
      taeFeatures.instantSearch = 'checked';
    }
    if (features?.filterCollection) {
      taeFeatures.filterCollection = 'checked';
    }
    if (features?.filterSearch) {
      taeFeatures.filterSearch = 'checked';
    }

    const formData = {
      themeId: themeCheckCompatibleId,
      taeFeatures
    };

    if (themeCheckCompatible.taeFeatures) {
      if (_.get(themeCheckCompatible, 'taeFeatures.recommendation', '') === 'installed') {
        formData.taeFeatures.recommendation = 'installed';
      }
      if (_.get(themeCheckCompatible, 'taeFeatures.instantSearch', '') === 'installed') {
        formData.taeFeatures.instantSearch = 'installed';
      }
      if (_.get(themeCheckCompatible, 'taeFeatures.filterCollection', '') === 'installed') {
        formData.taeFeatures.filterCollection = 'installed';
      }
      if (_.get(themeCheckCompatible, 'taeFeatures.filterSearch', '') === 'installed') {
        formData.taeFeatures.filterSearch = 'installed';
      }
      if (_.get(themeCheckCompatible, 'taeFeatures.filterLayout.collection', '') !== '') {
        formData.taeFeatures.filterLayout.collection = _.get(
          themeCheckCompatible,
          'taeFeatures.filterLayout.collection',
          ''
        );
        formData.taeFeatures.filterLayout.search = _.get(
          themeCheckCompatible,
          'taeFeatures.filterLayout.collection',
          ''
        );
      }
      if (_.get(themeCheckCompatible, 'taeFeatures.filterLayout.search', '') !== '') {
        formData.taeFeatures.filterLayout.collection = _.get(
          themeCheckCompatible,
          'taeFeatures.filterLayout.search',
          ''
        );
        formData.taeFeatures.filterLayout.search = _.get(
          themeCheckCompatible,
          'taeFeatures.filterLayout.search',
          ''
        );
      }
      if (
        themeCheckCompatible?.taeFeatures?.recommendationWidgets &&
        _.keys(themeCheckCompatible?.taeFeatures?.recommendationWidgets).length > 0
      ) {
        formData.taeFeatures.recommendationWidgets =
          themeCheckCompatible?.taeFeatures?.recommendationWidgets;
      }
    }

    if (!formData.taeFeatures.recommendation) {
      delete formData.taeFeatures.recommendationWidgets;
    }
    if (!formData.taeFeatures.filterCollection && !formData.taeFeatures.filterSearch) {
      delete formData.taeFeatures.filterLayout;
    }

    this.setState(
      {
        isSettingUp: true
      },
      async () => {
        await setupThemeV3(formData, null, i18n);

        await trackEvent('csatFeedback', 'taeSetup', 'csatFeedback_taeSetup');

        // Track Full Story event
        trackFSEvent('Setup New Theme', {
          feature: 'Theme Setup',
          selectedTheme: this.state.formData?.theme?.name,
          recommendationWidget: formData?.taeFeatures?.recommendation === 'checked',
          collectionPageFilters: formData?.taeFeatures?.filterCollection === 'checked',
          searchPageFilters: formData?.taeFeatures?.filterSearch === 'checked',
          instantSearchWidget: formData?.taeFeatures?.instantSearch === 'checked'
        });
      }
    );
  };

  handleContinueWarning = () => {
    const { step } = this.state;
    this.setState({
      step: step + 1,
      activeModalWarningLiveTheme: false
    });
  };

  handleGotoOnlineStoreThemes = () => {
    const onlineStoreThemes = getFullAppUrlThemes();
    window.open(onlineStoreThemes, '_blank');
  };

  handleContactWarning = () => {
    this.toggleChangeState('activeModalWarningOS10');
    this.toggleChangeState('activeModalContactUs');
  };

  backFromComplete = () => {
    const { handleThemeSetupChangeScreen } = this.props;
    handleThemeSetupChangeScreen(TypeScreen.List);
  };

  // Handle preview filter action
  handlePreview = async (id: string) => {
    const { trackEvent, getChecklist } = this.props;

    const previewUrl = getThemeReviewUrl(id);
    // Call to tracking event
    await trackEvent('themeSetup', 'preview', ' ');

    // Track Full Story event
    trackFSEvent('Preview theme', {
      feature: 'Theme Setup',
      selectedTheme: this.state.formData?.theme?.name
    });

    await getChecklist();

    window.open(previewUrl, '_blank');
  };

  handleGotoThemeEditor = async (id: string) => {
    const previewUrl = getFullAppUrlThemeEditor(id);
    window.open(previewUrl, '_blank');
  };

  render() {
    const {
      isNextableStep,
      step,
      isSetuppable,
      allRecommendationWidgets,
      activeModalWarningLiveTheme,
      activeModalWarningOS10,
      activeModalConfirmLeavePage,
      activeModalContactUs,
      isSettingUp,
      page,
      formData,
      isBackAfterSubmit,
      isDismissBannerComplete
    } = this.state;
    const { i18n, history, generalFormDirty, themeSelected, checkThemeLoading, tenantApp } =
      this.props;
    const enableSetupOnLiveTheme = enableThemeSetupV3OnLiveTheme(tenantApp);

    const feature = _.get(formData, 'feature', {});
    const isHasRecommendationFeature = !!feature.recommendation;

    return (
      <div className={`ThemeSetupScreenSetup ${isSettingUp ? 'isSettingUp' : ''}`}>
        {page === PageType.SetupForm && (
          <Page
            title={
              <div className="theme-setup-heading">
                <Stack spacing="tight">
                  <span>{i18n.translate('AdminHTML.ThemeSetup.NewThemeSetup.title_Setup')}</span>
                </Stack>
              </div>
            }
            breadcrumbs={[
              {
                content: '',
                onAction: () => {
                  if (isSettingUp) {
                  } else {
                    this.handleClickCancel();
                  }
                }
              }
            ]}
            primaryAction={
              <div className="theme-setup-menu">
                <div className="theme-setup-menu-action">
                  {step === 1 && (
                    <>
                      <Button onClick={() => this.confirmLeave(TypeScreen.List)}>
                        {i18n.translate('AdminHTML.Common.Action.cancel')}
                      </Button>
                      <Button
                        primary
                        disabled={checkThemeLoading || (isNextableStep ? false : true)}
                        onClick={this.onNext}>
                        {i18n.translate('AdminHTML.Common.Action.next')}
                      </Button>
                    </>
                  )}
                  {step === 2 && (
                    <>
                      <Button
                        disabled={isSettingUp}
                        onClick={() => this.confirmLeave(TypeScreen.List)}>
                        {i18n.translate('AdminHTML.Common.Action.cancel')}
                      </Button>
                      <Button
                        loading={isSettingUp}
                        primary
                        disabled={isSetuppable ? false : true}
                        onClick={this.onSetup}>
                        {i18n.translate('AdminHTML.ThemeSetup.NewThemeSetup.step3_BtnRunSetup')}
                      </Button>
                    </>
                  )}
                </div>
              </div>
            }>
            <Layout>
              <Layout.Section></Layout.Section>
              <Layout.Section>
                <ThemeSetupForm
                  themeSelected={themeSelected}
                  formData={formData}
                  isBackAfterSubmit={isBackAfterSubmit}
                  onCheckNextableStep={this.onCheckNextableStep}
                  step={step}
                  allRecommendationWidgets={allRecommendationWidgets}
                  backStep={this.handleClickCancel}></ThemeSetupForm>
              </Layout.Section>
              <Layout.Section>
                <p>
                  {i18n.translate('AdminHTML.ThemeSetup.NewThemeSetup.noteText', {
                    noteText_manual_guide: (
                      <span className="img-new-tab">
                        <Link external url={`${URL['ManualSetupV3']}`}>
                          {i18n.translate(
                            'AdminHTML.ThemeSetup.NewThemeSetup.noteText_manual_guide'
                          )}
                          <Icon source={ExternalMinor} color="interactive" />
                        </Link>
                      </span>
                    )
                  })}
                </p>
                <div className="theme-setup-menu" style={{ marginTop: '24px' }}>
                  <div className="theme-setup-menu-action">
                    {step === 1 && (
                      <>
                        <Button onClick={() => this.confirmLeave(TypeScreen.List)}>
                          {i18n.translate('AdminHTML.Common.Action.cancel')}
                        </Button>
                        <Button
                          primary
                          disabled={checkThemeLoading || (isNextableStep ? false : true)}
                          onClick={this.onNext}>
                          {i18n.translate('AdminHTML.Common.Action.next')}
                        </Button>
                      </>
                    )}
                    {step === 2 && (
                      <>
                        <Button
                          disabled={isSettingUp}
                          onClick={() => this.confirmLeave(TypeScreen.List)}>
                          {i18n.translate('AdminHTML.Common.Action.cancel')}
                        </Button>
                        <Button
                          loading={isSettingUp}
                          primary
                          disabled={isSetuppable ? false : true}
                          onClick={this.onSetup}>
                          {i18n.translate('AdminHTML.ThemeSetup.NewThemeSetup.step3_BtnRunSetup')}
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </Layout.Section>
            </Layout>
            <WarningLiveThemeModal
              active={activeModalWarningLiveTheme}
              handleSelectAnother={() => this.toggleChangeState('activeModalWarningLiveTheme')}
              handleGotoOnlineStoreThemes={this.handleGotoOnlineStoreThemes}
              handleSetupOnLiveTheme={this.handleSetupOnLiveTheme}
              enableSetupOnLiveTheme={enableSetupOnLiveTheme}
            />
            <WarningOS10Modal
              active={activeModalWarningOS10}
              handleSelectAnother={() => this.toggleChangeState('activeModalWarningOS10')}
              handleContact={this.handleContactWarning}
            />
            <ContactUsModal
              active={activeModalContactUs}
              handleCancel={() => this.toggleChangeState('activeModalContactUs')}
            />
            {/* This modal is used for when click any element on this page, but route not change */}
            <ConfirmLeaveModal
              active={activeModalConfirmLeavePage}
              handleCancel={this.closeModal}
              handleDiscard={this.handleDiscard}
            />
            {/* This modal is used for when click any link on this page, the route will change */}
            <RouteLeavingGuard
              when={generalFormDirty}
              navigate={path => {
                if (path === routes.themeSetup.view()) {
                  this.props.gotoThemeList();
                } else {
                  history.push(path);
                }
              }}
              shouldBlockNavigation={generalFormDirty}
            />
          </Page>
        )}
        {page === PageType.Success && (
          <Page
            title={
              <div className="theme-setup-heading">
                <Stack spacing="tight">
                  <span>
                    {i18n.translate('AdminHTML.ThemeSetup.NewThemeSetup.complete_Success_title')}
                  </span>
                </Stack>
              </div>
            }
            breadcrumbs={[
              {
                content: i18n.translate('AdminHTML.Recommendation.WidgetItem.breadcrumb'),
                onAction: this.backFromComplete
              }
            ]}>
            <Layout>
              <Layout.Section></Layout.Section>
              <Layout.Section>
                {!isDismissBannerComplete && (
                  <Banner
                    title={i18n.translate(
                      'AdminHTML.ThemeSetup.NewThemeSetup.complete_Success_title_banner'
                    )}
                    status="success"
                    onDismiss={() => this.toggleChangeState('isDismissBannerComplete')}>
                    <div className="theme-setup-v3-complete">
                      <div className="content">
                        {i18n.translate(
                          'AdminHTML.ThemeSetup.NewThemeSetup.complete_Success_content1'
                        )}
                        {isHasRecommendationFeature ? (
                          <div>
                            {i18n.translate(
                              'AdminHTML.ThemeSetup.NewThemeSetup.complete_Success_content1_recommendation',
                              {
                                t: (
                                  <span className="img-new-tab">
                                    <u
                                      onClick={() =>
                                        this.handleGotoThemeEditor(_.get(formData, 'theme.id', ''))
                                      }>
                                      {i18n.translate(
                                        'AdminHTML.ThemeSetup.NewThemeSetup.complete_Success_content1_recommendation_yourtheme'
                                      )}
                                      <Icon source={ExternalMinor} color="base" />
                                    </u>
                                  </span>
                                )
                              }
                            )}
                          </div>
                        ) : (
                          <span>
                            {' '}
                            {i18n.translate(
                              'AdminHTML.ThemeSetup.NewThemeSetup.complete_Success_content1_without_recommendation'
                            )}
                          </span>
                        )}
                      </div>
                      <div className="content">
                        {i18n.translate(
                          'AdminHTML.ThemeSetup.NewThemeSetup.complete_Success_content2',
                          {
                            t1: (
                              <span className="img-new-tab">
                                <Link external url={`${URL['ThemSetupFAQ']}`}>
                                  {i18n.translate(
                                    'AdminHTML.ThemeSetup.NewThemeSetup.complete_Success_content2_faq'
                                  )}
                                  <Icon source={ExternalMinor} color="base" />
                                </Link>
                              </span>
                            )
                          }
                        )}
                      </div>
                      <div className="content">
                        {i18n.translate(
                          'AdminHTML.ThemeSetup.NewThemeSetup.complete_Success_content3',
                          {
                            t1: (
                              <b>
                                {i18n.translate(
                                  'AdminHTML.ThemeSetup.NewThemeSetup.complete_Success_content3_t1'
                                )}
                              </b>
                            ),
                            theme_name: <b>{_.get(formData, 'theme.name', '')}</b>
                          }
                        )}
                      </div>
                      <div className="theme-setup-v3-complete-action">
                        <Button onClick={() => this.handlePreview(_.get(formData, 'theme.id', ''))}>
                          {i18n.translate(
                            'AdminHTML.ThemeSetup.NewThemeSetup.complete_Success_btn_preview'
                          )}
                        </Button>
                        {isHasRecommendationFeature ? (
                          <TextStyle>
                            <u
                              onClick={() =>
                                this.handleGotoThemeEditor(_.get(formData, 'theme.id', ''))
                              }>
                              {i18n.translate(
                                'AdminHTML.ThemeSetup.NewThemeSetup.complete_Success_goto_theme_editor'
                              )}
                            </u>
                          </TextStyle>
                        ) : (
                          <TextStyle>
                            <u
                              onClick={() =>
                                history.push(
                                  routes.themeSetting.editVisualDesign(
                                    _.get(formData, 'theme.id', '')
                                  )
                                )
                              }>
                              {i18n.translate(
                                'AdminHTML.ThemeSetup.NewThemeSetup.complete_Success_goto_visual_design'
                              )}
                            </u>
                          </TextStyle>
                        )}
                      </div>
                    </div>
                  </Banner>
                )}
              </Layout.Section>
            </Layout>
            {/* This modal is used for when click any link on this page, the route will change */}
            <RouteLeavingGuard
              when={page === PageType.Success}
              navigate={path => {
                if (path === routes.themeSetup.view()) {
                  this.props.gotoThemeList();
                } else {
                  history.push(path);
                }
              }}
              isGotoWithoutShowModalConfirm={true}
              shouldBlockNavigation={page === PageType.Success}
            />
          </Page>
        )}
        {page === PageType.Unsuccess && (
          <Page
            title={
              <div className="theme-setup-heading">
                <Stack spacing="tight">
                  <span>
                    {i18n.translate('AdminHTML.ThemeSetup.NewThemeSetup.complete_Unsuccess_title')}
                  </span>
                </Stack>
              </div>
            }
            breadcrumbs={[
              {
                content: i18n.translate('AdminHTML.Recommendation.WidgetItem.breadcrumb'),
                onAction: this.backFromComplete
              }
            ]}>
            <Layout>
              <Layout.Section></Layout.Section>
              <Layout.Section>
                {!isDismissBannerComplete && (
                  <Banner
                    title={i18n.translate(
                      'AdminHTML.ThemeSetup.NewThemeSetup.complete_Unsuccess_title_banner'
                    )}
                    status="critical"
                    onDismiss={() => this.toggleChangeState('isDismissBannerComplete')}>
                    <div className="theme-setup-v3-complete">
                      <div className="content">
                        {i18n.translate(
                          'AdminHTML.ThemeSetup.NewThemeSetup.complete_Unsuccess_content',
                          {
                            t1: (
                              <span className="img-new-tab">
                                <Link external url={`${URL['ThemSetupFAQ']}`}>
                                  {i18n.translate(
                                    'AdminHTML.ThemeSetup.NewThemeSetup.complete_Unsuccess_content_faq'
                                  )}
                                  <Icon source={ExternalMinor} color="base" />
                                </Link>
                              </span>
                            )
                          }
                        )}
                      </div>
                      <div className="theme-setup-v3-complete-action">
                        <Button onClick={() => this.toggleChangeState('activeModalContactUs')}>
                          {i18n.translate(
                            'AdminHTML.ThemeSetup.NewThemeSetup.complete_Unsuccess_contact'
                          )}
                        </Button>
                      </div>
                    </div>
                  </Banner>
                )}
              </Layout.Section>
            </Layout>
            <ContactUsModal
              active={activeModalContactUs}
              handleCancel={() => this.toggleChangeState('activeModalContactUs')}
            />
            {/* This modal is used for when click any link on this page, the route will change */}
            <RouteLeavingGuard
              when={page === PageType.Unsuccess}
              navigate={path => {
                if (path === routes.themeSetup.view()) {
                  this.props.gotoThemeList();
                } else {
                  history.push(path);
                }
              }}
              isGotoWithoutShowModalConfirm={true}
              shouldBlockNavigation={page === PageType.Unsuccess}
            />
          </Page>
        )}
      </div>
    );
  }
}

const mapStateToProps = (state, props) => {
  const { themeSetupV3 = {} } = state;
  const allRecommendationWidgets = _.get(state.recommendation, 'recommendationWidgets', []);
  const generalFormDirty = isDirty('ThemeSetup')(state);
  const setupThemeSuccess = _.get(state.themeSetupV3, 'setupThemeSuccess', undefined);

  return {
    allRecommendationWidgets,
    generalFormDirty,
    setupThemeSuccess,
    checkThemeLoading: _.get(themeSetupV3, 'checkThemeLoading', false),
    tenantApp: _.get(state, 'oauth.tenantApp', null),
    themeCheckCompatible: _.get(themeSetupV3, 'themeCheck', null),
    themeCheckCompatibleId: _.get(themeSetupV3, 'themeCheckId', null)
  };
};

const mapDispatchToProps = (dispatch, ownProps) => ({
  trackEvent: (eventSource, eventName, label) =>
    dispatch(checklistActions.trackEvent(eventSource, eventName, label)),
  getChecklist: () => dispatch(checklistActions.getChecklist(ownProps.i18n)),
  getRecommendationWidgets: () =>
    dispatch(recommendationAction.getRecommendationWidgets(ownProps.i18n)),
  setupThemeV3: (body: Object) => dispatch(themeSetupV3Action.setupThemeV3(body, ownProps.i18n)),
  getAllThemes: () => dispatch(themeSetupV3Action.getAllThemes(ownProps.i18n))
});

export default connect(mapStateToProps, mapDispatchToProps)(withI18n()(ThemeSetupScreenSetup));
